"use client";

import { useEffect } from "react";
import { Button } from "@frontend/shared/components/ui/moving-border";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import {
  useInsuranceConnectorSend,
  useInsuranceConnectorState,
} from "./insurance-connector-provider";
import type { ContentOverrides } from "./machine/types";

interface InsuranceConnectInlineProps {
  contentOverrides?: ContentOverrides;
}

/**
 * Lightweight inline component that renders the "Connect Insurance" button
 * within page content. Uses the layout-level InsuranceConnectorProvider
 * (does NOT create its own provider, unlike InsuranceConnector).
 *
 * Once the user has either connected insurance or opted into the
 * unverified -> pending prices flow, the same slot keeps showing a
 * "Browse treatments" CTA that scrolls past the hero instead of
 * reopening the modal.
 *
 * Accepts optional `contentOverrides` from the render config to customize
 * the eligibility welcome and intro screen copy.
 */
export function InsuranceConnectInline({
  contentOverrides,
}: InsuranceConnectInlineProps) {
  const state = useInsuranceConnectorState();
  const send = useInsuranceConnectorSend();
  const t = useTranslations("InsuranceConnector");

  const overridesKey = contentOverrides
    ? JSON.stringify(contentOverrides)
    : null;

  useEffect(() => {
    if (!contentOverrides || !overridesKey) return;
    send({ type: "SET_CONTENT_OVERRIDES", data: contentOverrides });
  }, [overridesKey, contentOverrides, send]);

  const hasCompletedFlow =
    state.matches("minimized.successConfirmation") ||
    state.matches("connected") ||
    !!state.context.isPendingPricesMode;

  // Hide while a modal UI is actively open AND the user hasn't completed the
  // flow yet. Once completed, the button persists through any minimized UI
  // (success banner, insurance menu, disconnect confirm).
  if (state.context.uiMode !== "closed" && !hasCompletedFlow) return null;

  const handleClick = () => {
    if (hasCompletedFlow) {
      window.scrollBy({ top: window.innerHeight, behavior: "smooth" });
    } else {
      send({ type: "OPEN" });
    }
  };

  const label = hasCompletedFlow
    ? t("BrowseTreatments")
    : t("ConnectInsurance");

  return (
    <div className="flex justify-center items-center py-8">
      <Button
        hoverAnimation
        icon
        variant="dark"
        borderRadius="3rem"
        containerClassName="h-[70px] w-auto text-[20px] font-medium"
        onClick={handleClick}
        aria-label={label}
        duration={6000}
      >
        {label}
      </Button>
    </div>
  );
}
